import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { CalendarClock, Hand } from "lucide-react";

const STATUS_TXT = { ok: "preparada", limite: "sin búsquedas este mes", ocupado: "esperando a otra búsqueda" };

/**
 * v26b · Cómo quiere recibir los leads el usuario: buscar él cuando quiera, o una entrega automática cada día
 * (sale primero de su despensa). La automática se puede pausar (vacaciones, mucho trabajo): en pausa no se
 * entrega nada y no se pierde nada.
 */
export default function DeliveryMode({ avatars = [] }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState([]);

  useEffect(() => {
    api.get("/delivery-settings").then((r) => setD(r.data)).catch(() => setD(null));
    api.get("/my-leads/summary").then((r) => setMine(r.data.profiles || [])).catch(() => setMine([]));
  }, []);

  const save = async (patch) => {
    const next = { ...d, ...patch };
    if (next.mode === "auto" && !next.avatar_id && !next.avatar_name && avatars[0]) {
      next.avatar_id = avatars[0].id || "";
      next.avatar_name = avatars[0].name || "";
    }
    setBusy(true);
    try {
      const r = await api.put("/delivery-settings", next);
      setD(r.data);
      if (patch.mode === "auto") toast.success(`Cada día a partir de las ${r.data.hour}:00 te preparamos leads nuevos.`);
      else if (patch.active === false) toast.success("Entrega en pausa. No se pierde nada: te lo guardamos.");
      else if (patch.active === true) toast.success("Entrega reactivada.");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (!d) return null;
  const auto = d.mode === "auto";
  const opt = (on) => `flex-1 rounded-md border px-3 py-2 text-left text-xs ${on ? "border-brand-accent bg-brand-accent/[0.08] text-brand-ink" : "border-brand-line text-brand-muted hover:border-brand-accent/60"}`;

  return (
    <section className="ca-card p-5" data-testid="delivery-mode">
      <p className="text-sm font-bold text-brand-ink">Cómo quieres recibir tus leads</p>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <button type="button" disabled={busy} onClick={() => save({ mode: "manual" })} aria-pressed={!auto} data-testid="delivery-manual" className={opt(!auto)}>
          <b className="flex items-center gap-1.5"><Hand size={13} /> Busco yo</b>
          <span className="mt-0.5 block">Lanzas una búsqueda cuando quieras.</span>
        </button>
        <button type="button" disabled={busy || avatars.length === 0 || d.auto_allowed === false} onClick={() => save({ mode: "auto", active: true })} aria-pressed={auto} data-testid="delivery-auto" className={`${opt(auto)} disabled:cursor-not-allowed disabled:opacity-60`}>
          <b className="flex items-center gap-1.5"><CalendarClock size={13} /> Entrega automática cada día</b>
          <span className="mt-0.5 block">
            {d.auto_allowed === false
              ? "Incluida en el plan Pro."
              : "Te preparamos leads nuevos cada mañana. Cuenta como una búsqueda al día."}
          </span>
        </button>
      </div>
      {d.auto_allowed === false && (
        <p className="mt-2 text-xs text-brand-muted" data-testid="delivery-upgrade">
          Con el plan Pro te preparamos leads nuevos cada mañana. <a href="/app/cuenta" className="font-semibold text-brand-accent hover:underline">Ver planes</a>
        </p>
      )}
      {auto && (
        <div className="mt-3 flex flex-wrap items-center gap-3 text-xs">
          <label className="flex items-center gap-2 text-brand-muted">Perfil
            <select className="ca-select" value={d.avatar_id || d.avatar_name} disabled={busy} data-testid="delivery-avatar"
              onChange={(e) => { const a = avatars.find((x) => (x.id || x.name) === e.target.value); if (a) save({ avatar_id: a.id || "", avatar_name: a.name }); }}>
              {avatars.map((a) => <option key={a.id || a.name} value={a.id || a.name}>{a.name}</option>)}
            </select>
          </label>
          <label className="inline-flex cursor-pointer items-center gap-2 text-brand-ink" data-testid="delivery-active">
            <input type="checkbox" role="switch" checked={!!d.active} disabled={busy} onChange={(e) => save({ active: e.target.checked })} style={{ accentColor: "var(--ca-gold)" }} data-testid="delivery-active-switch" />
            {d.active ? "Activada" : "En pausa"}
          </label>
          {d.last_date && <span className="text-brand-muted" data-testid="delivery-last">Última: {d.last_date.split("-").reverse().join("/")}{STATUS_TXT[d.last_status] ? ` · ${STATUS_TXT[d.last_status]}` : ""}</span>}
        </div>
      )}
      {mine.length > 0 && (
        <div className="mt-4 border-t border-brand-line pt-3" data-testid="my-leads">
          <p className="text-xs font-bold text-brand-ink">Tus leads por perfil</p>
          <ul className="mt-1.5 space-y-1 text-xs text-brand-muted">
            {mine.map((m) => (
              <li key={m.avatar_id || m.avatar_name} data-testid={`my-leads-${m.avatar_id || m.avatar_name}`}>
                <span className="font-semibold text-brand-ink">{m.avatar_name}</span> · {m.entregados} entregados · {m.guardados} guardados para tus próximas búsquedas
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
